import { useEffect, useMemo, useState } from "react";
import InputSearch from "../components/InputSearch";
import ProjectDetailsDrawer from "../components/ProjectDetailsDrawer";
import TabButton from "../components/TabButton";
import usePermission from "../hooks/usePermission";
import { TProjectData } from "../types/project";
import { GetAntIcon } from "../utils/ant_icons";
import MultiAssetKanbanBoard from "./MultiAssetKanbanBoard";
import NoAccess from "./NoAccess";
import { useMultiAssetStore } from "../stores/multiAssetStore";
import { Button, Flex, Switch } from "antd";
import CheckboxDropdown from "../components/CheckboxDropdown";
import useFilterCheckbox from "../hooks/useFilterCheckbox";
import SharepointComponent from "../components/SharepointComponent";
import { createWellExaminationApi } from "../services/api-server/wellExamination";
import { useTenantStore } from "../stores/tenantStore";

function MultiAssetProjectContainer({
	projectData,
}: {
	projectData: TProjectData;
}) {
	// Permissions
	const hasEditProjectPermission = usePermission(
		"Edit.Project",
		projectData?.project_code
	);
	const hasViewProjectPermission = usePermission(
		"View.Project",
		projectData?.project_code
	);
	const hasDeleteProjectPermission = usePermission(
		"Delete.Project",
		projectData?.project_code
	);

	const [activePage, setActivePage] = useState("kanban");
	const {
		setSearchValue,
		selectedFilters,
		setSelectedFilters,
		hideExcluded,
		setHideExcluded,
	} = useMultiAssetStore();

	const [filterMode, setFilterMode] = useState(false);
	const [sharepointDisabled, setSharepointDisabled] = useState<boolean>(true);
	const [wellExaminationDisabled, setWellExaminationDisabled] =
		useState<boolean>(true);
	const tenantKey = useTenantStore((state) => state.tenant?.value);
	const showWellExamination = projectData?.project_area === "Wells";
	const wellExaminationApi = useMemo(
		() =>
			showWellExamination && tenantKey && projectData?.project_code
				? createWellExaminationApi(tenantKey, projectData.project_code)
				: null,
		[showWellExamination, tenantKey, projectData?.project_code]
	);

	const { handleMenuClick, handleClearAllFilters } = useFilterCheckbox();

	// Clear filters when component unmounts or when activePage changes
	useEffect(() => {
		return () => {
			// Clear filters when component unmounts
			setSelectedFilters({});
			setSearchValue("");
		};
	}, [setSelectedFilters, setSearchValue]);

	// Clear filters when activePage changes
	useEffect(() => {
		setSelectedFilters({});
		setSearchValue("");
		setFilterMode(false);
	}, [activePage, setSelectedFilters, setSearchValue]);

	// If user does not have permission to view project, return empty
	if (hasViewProjectPermission.isLoading) {
		return <></>;
	}

	if (!hasViewProjectPermission.hasPermission) {
		return (
			<NoAccess text="Oops, looks like you don't have the authorisation to view projects." />
		);
	}

	const projectRoleAssignmentList = [
		// { label: "Project manager", value: "project_manager" },
		...projectData?.role_assignments?.map((role: any) => {
			return { label: role.role, value: role.role_id };
		}),
	];

	const handleFilterChange = (field: string, selectedValues: any[]) => {
		handleMenuClick(field, selectedValues);
		const newFilters = { ...selectedFilters, [field]: selectedValues };
		setSelectedFilters(newFilters);
	};

	const handleClearFilters = () => {
		handleClearAllFilters();
		setSelectedFilters({});
	};

	return (
		<div className="generic-container">
			<ProjectDetailsDrawer
				projectData={projectData}
				permissions={{
					hasEditProjectPermission,
					hasDeleteProjectPermission,
				}}
			/>

			<div
				className="project-header"
				style={{ justifyContent: "space-between" }}
			>
				<div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
					<TabButton
						identifier="kanban"
						label="Kanban"
						icon={GetAntIcon("project")}
						activePage={activePage}
						setActivePage={setActivePage}
					/>
					<TabButton
						identifier="gantt-chart"
						label="Gantt chart"
						icon={GetAntIcon("gantt")}
						activePage={activePage}
						setActivePage={setActivePage}
						disabled={true}
					/>
					<TabButton
						identifier="documents"
						label="Documents"
						icon={GetAntIcon("file2")}
						activePage={activePage}
						setActivePage={setActivePage}
						tooltip={{
							title: sharepointDisabled ? "Documents are disabled" : "",
							placement: "top",
						}}
						disabled={sharepointDisabled}
					/>
					{wellExaminationApi && (
						<TabButton
							identifier="well-examination"
							label="Well examination"
							icon={GetAntIcon("file2")}
							activePage={activePage}
							setActivePage={setActivePage}
							tooltip={{
								title: wellExaminationDisabled
									? "Well examination documents are disabled"
									: "",
								placement: "top",
							}}
							disabled={wellExaminationDisabled}
						/>
					)}
				</div>
				<Flex gap={"15px"}>
					{activePage === "kanban" && (
						<InputSearch
							placeholder="Search by task name"
							setSearchValue={setSearchValue}
						/>
					)}
					{activePage !== "documents" && activePage !== "well-examination" && (
						<div
							className={filterMode ? "project-active-button" : ""}
							style={{
								padding: "4px 10px",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
							}}
							onClick={() => {
								setFilterMode(!filterMode);
							}}
						>
							{GetAntIcon("filter")}
						</div>
					)}
				</Flex>
			</div>
			{/* <div style={{ overflow: "auto", height: "100%" }}> */}
			<div
				className="generic-content project-overview-container"
				style={{
					flex: 1,
					display: "flex",
					flexWrap: "inherit",
					flexDirection: "column",
					alignItems: "flex-start",
					padding: 0,
					gap: 0,
					overflowX: "auto",
					maxHeight: "100%",
				}}
			>
				<div
					className={`filter-area ${
						filterMode ? "show-filter" : "hide-filter"
					}`}
				>
					{filterMode && (
						<>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									gap: "10px",
									padding: "8px",
									// marginRight: "20px",
									alignItems: "center",
									height: "40px",
								}}
							>
								Hide excluded items
								<Switch
									defaultValue={hideExcluded}
									onChange={(checked: any) => {
										setHideExcluded(checked);
									}}
								/>
							</div>
							{activePage === "kanban" && (
								<>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("responsible_roles", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.responsible_roles}
										Key="responsible_roles"
										label="Responsible roles"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("accountable_roles", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.accountable_roles}
										Key="accountable_roles"
										label="Accountable roles"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("consulted", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.consulted}
										Key="consulted"
										label="Consulted"
									/>
									<CheckboxDropdown
										options={projectRoleAssignmentList}
										onChange={(selectedValues) =>
											handleFilterChange("informed_parties", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.informed_parties}
										Key="informed_parties"
										label="Informed parties"
									/>
									<CheckboxDropdown
										options={[
											{ label: "New", value: "New" },
											{
												label: "To do",
												value: "To do",
											},
											{ label: "In Progress", value: "In progress" },
											{ label: "Late", value: "Late" },
											{
												label: "Resolved",
												value: "Resolved",
											},
										]}
										onChange={(selectedValues) =>
											handleFilterChange("status", selectedValues)
										}
										onClear={""}
										defaultValue={selectedFilters.status}
										Key="status"
										label="Status"
									/>
									<div className="test">
										<Button type="link" onClick={handleClearFilters}>
											Reset All
										</Button>
									</div>
								</>
							)}
						</>
					)}
				</div>
				{activePage == "kanban" && (
					<MultiAssetKanbanBoard project={projectData} />
				)}
				{activePage == "gantt-chart" && <div>Gantt chart</div>}

				<SharepointComponent
					setDisabled={setSharepointDisabled}
					type="project"
					id={projectData.project_code}
					name={projectData.project_name}
					visible={activePage == "documents"}
				/>
				{wellExaminationApi && (
					<SharepointComponent
						setDisabled={setWellExaminationDisabled}
						type="well-examination"
						id={projectData.project_code}
						name={projectData.project_name}
						api={wellExaminationApi}
						visible={activePage == "well-examination"}
					/>
				)}
			</div>
			{/* </div> */}
		</div>
	);
}

export default MultiAssetProjectContainer;
